import { createContext, useCallback, useContext, useState } from "react";
import * as Toast from "@radix-ui/react-toast";
import { CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";
import { cn } from "./cn";

type Variant = "success" | "warning" | "error" | "info";

interface ToastItem {
  id: number;
  variant: Variant;
  title: string;
  description?: string;
}

interface ToastApi {
  push: (t: Omit<ToastItem, "id">) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  warn: (title: string, description?: string) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((t: Omit<ToastItem, "id">) => {
    const id = nextId++;
    setItems((prev) => [...prev, { ...t, id }]);
  }, []);

  const api: ToastApi = {
    push,
    success: (title, description) => push({ variant: "success", title, description }),
    error: (title, description) => push({ variant: "error", title, description }),
    info: (title, description) => push({ variant: "info", title, description }),
    warn: (title, description) => push({ variant: "warning", title, description }),
  };

  return (
    <ToastCtx.Provider value={api}>
      <Toast.Provider duration={4500} swipeDirection="right">
        {children}
        {items.map((t) => {
          const Icon = t.variant === "success"
            ? CheckCircle2
            : t.variant === "error"
              ? XCircle
              : t.variant === "warning"
                ? AlertTriangle
                : Info;
          const tint =
            t.variant === "success"
              ? "border-success/40 text-success"
              : t.variant === "error"
                ? "border-danger/40 text-danger"
                : t.variant === "warning"
                  ? "border-warning/40 text-warning"
                  : "border-info/40 text-info";
          return (
            <Toast.Root
              key={t.id}
              className={cn(
                "panel-elevated border-l-2 px-3 py-2.5 flex items-start gap-3 min-w-[280px] max-w-[420px]",
                "data-[state=open]:animate-slide-in-right",
                tint,
              )}
              onOpenChange={(open) => {
                if (!open) setItems((prev) => prev.filter((x) => x.id !== t.id));
              }}
            >
              <Icon className="size-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <Toast.Title className="text-sm font-medium text-fg">{t.title}</Toast.Title>
                {t.description && (
                  <Toast.Description className="text-xs text-fg-muted mt-0.5 break-words">
                    {t.description}
                  </Toast.Description>
                )}
              </div>
              <Toast.Close className="text-fg-subtle hover:text-fg text-xs leading-none">✕</Toast.Close>
            </Toast.Root>
          );
        })}
        <Toast.Viewport className="fixed bottom-4 right-4 z-[400] flex flex-col gap-2 outline-none" />
      </Toast.Provider>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
